import React from 'react';
import {
    ExportOutlined,
    ImportOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { Button, Divider, Input, message, Modal, Table } from 'antd';
import { Api } from '../../../lib/api';


import '../styles.scss';
import { CUSTOM_FIELDS } from '../components/pageNameConstants';
import DeleteWarningModal from '../components/DeleteWarningModal';
import { omit } from 'lodash';
import { downloadFile, importFile } from '../../../lib/utils';

interface CustomFieldsState {
    customFields: Array<any>;
    newField: string;
    key: number;
    showDeleteModal: boolean;
    showModal: boolean;
    customFieldName?: string;
    customFieldRecord?: number;
    selectedRowKeys?: Array<any>;
    selectedRows?: Array<any>;
}

export default class CustomFields extends React.PureComponent<{}, CustomFieldsState> {
    state: CustomFieldsState = {
        customFields: [],
        newField: '',
        key: 0,
        showDeleteModal: false,
        showModal: false
    };

    componentDidMount() {
        this.getCustomFields();
    }

    getCustomFields = async () => {
        await Api.getCustomFields().then((customFields) => {
            this.setState({ customFields: customFields });
        });
    }

    deleteCustomField = async (id) => {
        let regexPattern = /\${Custom_Fields\.(.*?)}/;
        let field = await Api.getCustomField(id);
        let labels = await Api.getLabels();
        let labelsList: Array<any> = [];
        
        labels.forEach(label => {
            let docElements = label.reportSpec.report?.docElements;
            if(docElements !== undefined && docElements !== null) {
                docElements.forEach(docItem => {
                    let match = docItem.content?.match(regexPattern);
                    if(match && match[1] === field.name) {
                        labelsList.push(label);
                    }
                })
            }
        });

        if(labelsList.length > 0) {
            alert('This custom field is being used in the following labels and must be deleted there first:\n\t' + labelsList.map(label => label.name).join('\n\t'));
            return;
        }

        return Api.deleteCustomField(id);
    }

    save() {
        if (this.state.newField.toLowerCase() === 'length') {
            alert('Please, use different custom field name since length is reserved for the database');
        } else {
            Api.createCustomField(this.state.newField).then(() => {
                this.getCustomFields();
                this.setState((state) => {
                    return {
                        newField: '',
                        key: state.key + 1
                    };
                });
            });
        }
        this.setState({showModal: false})
    }

    closeDeleteModal = () => {
        this.setState({showDeleteModal: false});
    }
    render() {
        return (
            <div className="section page-card">
                <div className="create-button">
                    {
                        this.state.selectedRowKeys && this.state.selectedRowKeys.length > 0 
                            ? (
                                <>
                                    <Button
                                        shape='round'
                                        onClick={() => this.setState({ selectedRowKeys: [], selectedRows: [] })}
                                        style={{ marginRight: "0.5em" }}
                                    >
                                        Clear Selection
                                    </Button>
                                    <Button
                                        type="primary"
                                        shape="round"
                                        icon={<ExportOutlined />}
                                        onClick={() => {
                                            const selectedRowIds = this.state.selectedRows?.map(row => row.id);

                                            const selectedFields = this.state.customFields
                                                .filter(field => selectedRowIds?.includes(field.id))
                                                .map(field => ({ name: field.name }));

                                            let fileName = '';
                                            if(selectedFields.length === 1) {
                                                fileName = `custom_field-${selectedFields[0].name}.json`;
                                            } else if (selectedFields.length > 1) {
                                                fileName = 'selected_custom_fields.json';
                                            } else {
                                                message.error('No custom fields selected for export');
                                                return;
                                            }

                                            downloadFile(fileName, selectedFields, 'application/json');

                                            this.setState({ selectedRowKeys: [], selectedRows: [] });
                                        }}
                                    >
                                        Export Selected ({this.state.selectedRowKeys.length})
                                    </Button>
                                </>
                            )
                            : (
                                <>
                                    <Button 
                                        shape='round' 
                                        icon={<ImportOutlined />} 
                                        style={{ marginRight: "0.5em" }}
                                        onClick={async () => {
                                            importFile(async (file) => {
                                                let fileReader = new FileReader();

                                                fileReader.onload = async (e) => {
                                                    try {
                                                        const fileContent = JSON.parse(e.target?.result as string);

                                                        const { created, skipped } = await Api.importCustomFields({ customFields: fileContent });
                                                        
                                                        if(created.length > 0 && skipped.length > 0) {
                                                            message.success(`Imported ${created.length} custom fields. Skipped ${skipped.length} existing custom fields.`);
                                                        } else if (created.length > 0 && skipped.length === 0) {
                                                            message.success(`Successfully imported ${created.length} custom fields.`);
                                                        } else if (created.length === 0 && skipped.length > 0) {
                                                            message.info(`All ${skipped.length} custom fields were skipped as they already exist.`);
                                                        }

                                                        await this.getCustomFields();
                                                    } catch (error) {
                                                        message.error('Error reading file: ' + error);
                                                        return;
                                                    }
                                                };

                                                fileReader.readAsText(file);
                                            });
                                        }}
                                    >
                                        Import
                                    </Button>
                                    <Button type="primary" shape="round" icon={<PlusOutlined />} onClick={() => this.setState({showModal: true})}>
                                        New Custom Field
                                    </Button>
                                </>
                            )
                    }
                </div>
                <Table
                    columns={[
                        {
                            title: 'Name',
                            dataIndex: 'name',
                            key: 'name',
                            render: (text, record) => <div>{text}</div>
                        },
                        {
                            title: 'Action',
                            key: 'action',
                            render: (text, record) => (
                                <>
                                    {record.name !== 'Length' && (
                                        <div className="action-buttons">
                                            <Button
                                                type="link"
                                                style={{ color: '#40a9ff' }}
                                                onClick={() => {
                                                    downloadFile(
                                                        `custom_field-${record.name}.json`,
                                                        [{ name: record.name }],
                                                        'application/json'
                                                    );
                                                }}
                                            >
                                                Export
                                            </Button>
                                            <Divider type="vertical" />
                                            <Button
                                                type="link"
                                                style={{ color: '#e60013' }}
                                                onClick={() => {
                                                    this.setState({
                                                        showDeleteModal: true,
                                                        customFieldName: record.name,
                                                        customFieldRecord: record.id
                                                    })
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )
                        }
                    ]}
                    rowSelection={{
                        selectedRowKeys: this.state.selectedRowKeys,
                        onChange: (selectedRowKeys, selectedRows) => {
                            this.setState({ selectedRowKeys, selectedRows });
                        }
                    }}
                    dataSource={this.state.customFields}
                    pagination={{ pageSize: 8 }}
                    rowKey="name"
                />  
                <Modal
                    title={"New Custom Field"}
                    open={this.state.showModal}
                    onCancel={() => this.setState({showModal: false})}
                    footer={[
                        <Button disabled={this.state.newField === ''} onClick={() => this.save()}>
                            New Custom Field
                        </Button>
                    ]}
                >
                    <Input
                        key={this.state.key}
                        value={this.state.newField}
                        onChange={(e) => {
                            const re = /^[A-Za-z][A-Za-z0-9]*$/;

                            if (e.target.value === '' || re.test(e.target.value)) {
                                this.setState({ newField: e.target.value });
                            }
                        }}
                        placeholder="Custom field"
                    />{' '}
                </Modal>
                <DeleteWarningModal
                    isOpen={this.state.showDeleteModal}
                    itemTypeToDelete={CUSTOM_FIELDS}
                    itemNameToDelete={this.state.customFieldName || ''}
                    itemRecordToDelete={this.state.customFieldRecord || 0}
                    closeCallback={this.closeDeleteModal}
                    delete={async () => {
                        await this.deleteCustomField(this.state.customFieldRecord)
                        await this.getCustomFields();

                        this.setState({
                            selectedRows: this.state.selectedRows?.filter(row => row.name !== this.state.customFieldRecord),
                            selectedRowKeys: this.state.selectedRowKeys?.filter(key => key !== this.state.customFieldName)
                        })
                    }}
                    update={this.getCustomFields}
                >
                </DeleteWarningModal>
            </div>
        )
    }
}